import { Alert, AlertTheme } from '@vivino/js-react-common-ui';
import { CountryFlagIcon48, setShipTo } from '@vivino/js-web-common';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { getInputMaskByCountry, putMaskOn } from 'vivino-js/helpers/zipCode';

import ZipCodeInputPresentational from './ZipCodeInput.presentational';
import styles from './zipCodeInput.scss';

const LOCATION_RELOAD_DELAY = 2000;

interface IZipCodeInputProps {
  shippingZipCode: string;
  requireShippingZipText: string;
  countryCode: string;
  onZipChanged?: (val: string) => void;
  onZipValidated: (data: any) => void;
  onZipInvalidated?: () => void;
  state?: string;
  className: string;
  placeholder: string;
  useMask: boolean;
  maxLength?: number;
  showCountryFlag?: boolean;
}

const ZipCodeInput = ({
  shippingZipCode = '',
  requireShippingZipText = '',
  className,
  placeholder,
  countryCode,
  state,
  onZipChanged,
  onZipValidated,
  onZipInvalidated,
  useMask,
  maxLength,
  showCountryFlag,
}: IZipCodeInputProps) => {
  const [isValid, setValid] = useState(!!shippingZipCode);
  const [zipCode, setZipCode] = useState(putMaskOn(countryCode, shippingZipCode) || '');

  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const inputMask = getInputMaskByCountry(countryCode);

  const maxZipInputLength = useMask ? inputMask.mask.length : maxLength;

  useEffect(() => {
    if (zipCode?.length !== maxZipInputLength || errorMessage) {
      setValid(false);
      onZipInvalidated?.();
    }

    if (errorMessage) {
      setErrorMessage(errorMessage);
    } else if (requireShippingZipText && !zipCode) {
      setErrorMessage(requireShippingZipText);
      onZipInvalidated?.();
    }
  }, [zipCode]);

  const handleChange = (value: string) => {
    if (useMask) {
      value = putMaskOn(countryCode, value);
    }

    if (value?.length === maxZipInputLength) {
      validate(value);
    }

    onZipChanged?.(value);
    setZipCode(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validate(zipCode);
  };

  const validate = (zipCode) => {
    if (!zipCode) {
      return;
    }

    setIsValidating(true);

    setShipTo(countryCode, state, zipCode)
      .then((data) => {
        setErrorMessage('');
        setValid(true);

        setTimeout(() => {
          onZipValidated(data);
        }, LOCATION_RELOAD_DELAY);
      })
      .catch((err) => {
        setValid(false);
        setErrorMessage(err.message);
        onZipInvalidated?.();
      })
      .finally(() => {
        setIsValidating(false);
      });
  };

  return (
    <div className={cx(styles.zipCodeInput, className)}>
      {showCountryFlag && (
        <div className={styles.countryFlag}>
          <CountryFlagIcon48 countryCode={countryCode} />
        </div>
      )}
      <ZipCodeInputPresentational
        placeholder={placeholder}
        isValid={isValid}
        hasError={!!errorMessage}
        onSubmit={handleSubmit}
        onChange={(val) => handleChange(val)}
        value={zipCode || ''}
        maxLength={maxZipInputLength}
        showCountryFlag={showCountryFlag}
        isValidating={isValidating}
      />
      {errorMessage && (
        <Alert
          subdued
          icon={false}
          theme={AlertTheme.Critical}
          fullWidth
          className={styles.errorMessage}
        >
          {errorMessage}
        </Alert>
      )}
    </div>
  );
};

export default ZipCodeInput;
